import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  DateUtils,
  ToasterService,
  TranslateService,
} from '@devinforius/i-essentials';
import { IMap, IPoint } from '@devinforius/i-map';
import { ITab } from '@devinforius/i-data';
import { ITableColumn, ITableConfig } from '@devinforius/i-table';
import { environment } from '@environments/environment';
import { Constatation } from '@shared/models/constatation';
import { ConstatationService } from '@shared/services/constatation.service';
import { debounceTime, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-constatation-list',
  standalone: false,
  templateUrl: './constatation-list.component.html',
  styleUrl: './constatation-list.component.scss',
})
export class ConstatationListComponent implements OnInit, OnDestroy {
  private translater: TranslateService = inject(TranslateService);
  private toaster: ToasterService = inject(ToasterService);
  private router: Router = inject(Router);
  private constatationService: ConstatationService =
    inject(ConstatationService);

  private searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription: Subscription;

  public loading: boolean = false;
  public tabs!: ITab[];
  public columns!: ITableColumn[];
  public items: Constatation[] = [];
  public config!: ITableConfig;
  public today: Date = new Date();
  public currentSearch!: {
    type: 'date' | 'state' | 'all';
    value: string | Date | null;
    quickSearch?: string;
  } | null;
  public iMapConfig!: IMap;
  public iMapToken = environment.iMapToken;
  public points: IPoint[] = [];

  constructor() {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        if (this.currentSearch) {
          this.currentSearch.quickSearch = value;
        } else {
          this.currentSearch = {
            quickSearch: value,
            type: 'date',
            value: this.today,
          };
        }

        sessionStorage.setItem(
          'current-search',
          JSON.stringify(this.currentSearch),
        );
        this.constatationService.pagination.pageNumber = 1;
        this.loadConstatations();
      });
  }

  public ngOnInit(): void {
    this.initColumns();

    if (sessionStorage.getItem('current-search')) {
      this.currentSearch = JSON.parse(
        sessionStorage.getItem('current-search') ||
          "{ type: 'date', value: this.today }",
      );
    } else {
      this.currentSearch = { type: 'date', value: this.today };
    }

    this.initTabs();

    this.loadConstatations();
  }

  public goToDetail(event: any): void {
    this.router.navigate(['constatations', event['id']]);
  }

  public tabChange(event: string): void {
    if (event === 'pending') {
      this.currentSearch = { type: 'state', value: '1' };
    } else if (event === 'date') {
      this.currentSearch = { type: 'date', value: this.today };
    } else {
      this.currentSearch = { type: 'all', value: null };
    }

    sessionStorage.setItem(
      'current-search',
      JSON.stringify(this.currentSearch),
    );
    this.constatationService.pagination.pageNumber = 1;

    this.loadConstatations();
  }

  public pageChange(event: { first: number; rows: number }): void {
    this.constatationService.pagination.pageSize = event.rows;
    this.constatationService.pagination.pageNumber =
      event.first === 0 ? 1 : event.first / event.rows + 1;

    this.loadConstatations();
  }

  public ngOnDestroy(): void {
    this.constatationService.pagination = {
      pageNumber: 1,
      pageSize: 25,
      totalCount: 0,
    };

    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  public search(value: string): void {
    this.searchSubject.next(value);
  }

  // i-tabs (i-data) pilote l'onglet actif via [activeTab] (déduit de la recherche courante).
  public get currentTabId(): string {
    if (this.currentSearch?.type === 'state') {
      return 'pending';
    }
    if (this.currentSearch?.type === 'all') {
      return 'all';
    }
    return 'date';
  }

  private loadConstatations(): void {
    const params: { date?: string; stateId?: number; search?: string } = {};

    if (this.currentSearch) {
      if (this.currentSearch.type === 'date') {
        params['date'] = DateUtils.format(
          this.currentSearch.value as string,
          'YYYY-MM-DD',
        );
      }

      if (this.currentSearch.type === 'state') {
        params['stateId'] = 1; // Pour l'instant le filtre ne se fait que sur les constatations à traiter
      }

      if (this.currentSearch.quickSearch) {
        params['search'] = this.currentSearch.quickSearch;
      }
    }

    this.items = [];
    this.points = [];
    this.loading = true;

    this.constatationService.getAll(params).subscribe({
      next: (response: Constatation[]) => {
        const constatation = response.find(
          (item: Constatation) => item.locations.length > 0,
        );

        if (constatation) {
          this.iMapConfig = new IMap({
            zoom: 12,
            center: [
              constatation.locations[0].longitude,
              constatation.locations[0].latitude,
            ],
            showControls: false,
            showFullScreen: false,
            showGeolocate: false,
            showScaleControls: false,
          });
        }

        this.items = response;

        response.forEach((value: Constatation) => {
          if (value.locations?.length > 0) {
            value.locations.forEach((location) => {
              if (location.longitude && location.latitude) {
                this.points.push({
                  coordinates: location,
                  data: {
                    id: value.id,
                    offenceId: value.offenceId,
                    offenceType: value.offenceType,
                  },
                });
              }
            });
          }
        });

        this.config = new ITableConfig({
          lazy: true,
          tableStyle: { width: '100%' },
          height: '550px',
          isRowClickable: true,
          multiselect: false,
          showAdvancedSearch: false,
          showForSelectionRow: false,
          showColumnSelector: false,
          rowPerPageOptions: [25, 50, 100],
          pageSize: this.constatationService.pagination.pageSize,
          pageFirst:
            (this.constatationService.pagination.pageNumber - 1) *
            this.constatationService.pagination.pageSize,
          totalRecords: this.constatationService.pagination.totalCount,
        });

        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toaster.push(
          'error',
          this.translater.translate('global.error'),
          this.translater.translate('list.error.get-all'),
        );
      },
    });
  }

  private initTabs(): void {
    this.tabs = [
      {
        id: 'date',
        label: this.translater.translate('list.tabs.today'),
      },
      {
        id: 'pending',
        label: this.translater.translate('list.tabs.to-treat'),
      },
      {
        id: 'all',
        label: this.translater.translate('list.tabs.all'),
      },
    ];
  }

  private initColumns(): void {
    this.columns = [
      {
        field: 'id',
        header: this.translater.translate('constatation.id'),
      },
      {
        field: 'offenceId',
        header: this.translater.translate('constatation.offence-id'),
      },
      {
        field: 'offenceType',
        header: this.translater.translate('constatation.offence-type'),
      },
      {
        field: 'plate',
        header: this.translater.translate('constatation.plate'),
      },
      {
        field: 'plateCountryCode',
        header: this.translater.translate('constatation.plate-country'),
      },
      {
        field: 'stateCode',
        header: this.translater.translate('constatation.state-code'),
        format: (value: string) => {
          const translation = this.translater.translate(
            `constatation.${value.toLowerCase()}`,
          );
          const color =
            value === 'PENDING'
              ? '#ffaf2f'
              : value === 'APPROVED'
                ? '#3bde86'
                : '#ff6260';

          return `<div style="
          border: 1px solid ${color};
          border-radius: 4px; color: ${color};
          background-color: ${color}20;
          text-align: center;
          width: fit-content;
          padding: 0.25rem; 
          font-weight: 600">${translation}</div>`;
        },
      },
    ];
  }
}
